if (!customElements.get('cabane-sort-select')) {
  customElements.define(
    'cabane-sort-select',
    class CabaneSortSelect extends HTMLElement {
      connectedCallback() {
        if (this.abortController) return;

        if (!this.handlersBound) {
          this.nativeSelect = this.querySelector('[data-cabane-sort-native]');
          this.trigger = this.querySelector('.cabane-sort-select__trigger');
          this.menu = this.querySelector('.cabane-sort-select__menu');
          this.options = Array.from(this.querySelectorAll('.cabane-sort-select__option'));
          this.label = this.querySelector('[data-cabane-sort-label]');

          if (!this.nativeSelect || !this.trigger || !this.menu || !this.options.length || !this.label) return;

          this.onTriggerClick = this.onTriggerClick.bind(this);
          this.onTriggerKeyDown = this.onTriggerKeyDown.bind(this);
          this.onMenuClick = this.onMenuClick.bind(this);
          this.onMenuKeyDown = this.onMenuKeyDown.bind(this);
          this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
          this.onFocusOut = this.onFocusOut.bind(this);
          this.onNativeChange = this.syncFromNative.bind(this);
          this.handlersBound = true;
        }

        this.nativeSelect.setAttribute('aria-hidden', 'true');
        this.nativeSelect.tabIndex = -1;
        this.dataset.enhanced = 'true';

        this.abortController = new AbortController();
        const listenerOptions = { signal: this.abortController.signal };

        this.trigger.addEventListener('click', this.onTriggerClick, listenerOptions);
        this.trigger.addEventListener('keydown', this.onTriggerKeyDown, listenerOptions);
        this.menu.addEventListener('click', this.onMenuClick, listenerOptions);
        this.menu.addEventListener('keydown', this.onMenuKeyDown, listenerOptions);
        this.nativeSelect.addEventListener('change', this.onNativeChange, listenerOptions);
        this.addEventListener('focusout', this.onFocusOut, listenerOptions);
        document.addEventListener('pointerdown', this.onDocumentPointerDown, listenerOptions);

        this.syncFromNative();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.abortController = null;
        window.clearTimeout(this.typeaheadTimer);
      }

      get selectedOption() {
        return this.options.find((option) => option.dataset.value === this.nativeSelect.value) || this.options[0];
      }

      onTriggerClick() {
        this.isOpen() ? this.close() : this.open(this.selectedOption);
      }

      onTriggerKeyDown(event) {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            this.open(this.selectedOption);
            break;
          case 'ArrowUp':
            event.preventDefault();
            this.open(this.selectedOption);
            break;
          case 'Home':
            event.preventDefault();
            this.open(this.options[0]);
            break;
          case 'End':
            event.preventDefault();
            this.open(this.options[this.options.length - 1]);
            break;
          case 'Escape':
            if (!this.isOpen()) return;
            event.preventDefault();
            this.close(true);
            break;
        }
      }

      onMenuClick(event) {
        const option = event.target.closest('.cabane-sort-select__option');
        if (!option || !this.contains(option)) return;
        this.choose(option);
      }

      onMenuKeyDown(event) {
        const currentOption = event.target.closest('.cabane-sort-select__option');
        if (!currentOption) return;

        const currentIndex = this.options.indexOf(currentOption);

        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            this.options[(currentIndex + 1) % this.options.length].focus();
            break;
          case 'ArrowUp':
            event.preventDefault();
            this.options[(currentIndex - 1 + this.options.length) % this.options.length].focus();
            break;
          case 'Home':
            event.preventDefault();
            this.options[0].focus();
            break;
          case 'End':
            event.preventDefault();
            this.options[this.options.length - 1].focus();
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            this.choose(currentOption);
            break;
          case 'Escape':
            event.preventDefault();
            this.close(true);
            break;
          default:
            this.focusByTypeahead(event);
        }
      }

      onDocumentPointerDown(event) {
        if (this.isOpen() && !this.contains(event.target)) this.close();
      }

      onFocusOut() {
        requestAnimationFrame(() => {
          if (this.isOpen() && !this.contains(document.activeElement)) this.close();
        });
      }

      focusByTypeahead(event) {
        if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;

        this.typeahead = `${this.typeahead || ''}${event.key.toLocaleLowerCase('fr')}`;
        window.clearTimeout(this.typeaheadTimer);
        this.typeaheadTimer = window.setTimeout(() => {
          this.typeahead = '';
        }, 700);

        const match = this.options.find((option) =>
          option.textContent.trim().toLocaleLowerCase('fr').startsWith(this.typeahead)
        );

        if (match) {
          event.preventDefault();
          match.focus();
        }
      }

      choose(option) {
        const valueChanged = this.nativeSelect.value !== option.dataset.value;
        this.nativeSelect.value = option.dataset.value;
        this.syncFromNative();
        this.close(true);

        if (valueChanged) {
          this.nativeSelect.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }

      syncFromNative() {
        const selected = this.selectedOption;
        if (!selected) return;

        this.label.textContent = selected.textContent.trim();
        this.options.forEach((option) => {
          option.setAttribute('aria-selected', (option === selected).toString());
        });
      }

      open(optionToFocus) {
        this.menu.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        this.classList.add('is-open');
        requestAnimationFrame(() => (optionToFocus || this.selectedOption).focus());
      }

      close(restoreFocus = false) {
        this.menu.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        this.classList.remove('is-open');
        this.typeahead = '';
        window.clearTimeout(this.typeaheadTimer);
        if (restoreFocus) this.trigger.focus();
      }

      isOpen() {
        return this.trigger.getAttribute('aria-expanded') === 'true';
      }
    }
  );
}

